import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import BrandedHero from "../components/blog/BrandedHero";
import PreSettlementCalculator from "../components/blog/PreSettlementCalculator";
import { api } from "../lib/api";
import { useSEO } from "../lib/useSEO";
import { processArticleHtml } from "../lib/processArticleHtml";
import { isKnownBrokenHero } from "../lib/heroImage";
import { Button } from "../components/ui/button";
import { UNSERVED_STATE_GUIDES } from "../data/content";
import { ArrowLeft, ArrowRight, Calendar, Clock, Loader2 } from "lucide-react";

import { TableOfContents, MobileTableOfContents } from "../components/blog/TableOfContents";
import { QuickAnswer } from "../components/blog/QuickAnswer";
import { FAQAccordion } from "../components/blog/FAQAccordion";
import { RelatedCarousel } from "../components/blog/RelatedCarousel";
import { PrevNextNav } from "../components/blog/PrevNextNav";
import { ShareButtons } from "../components/blog/ShareButtons";
import { Breadcrumbs } from "../components/blog/Breadcrumbs";
import { AuthorBlock } from "../components/blog/AuthorBlock";

const SITE_URL = "https://instabridgefunding.com";

/** Deterministic 1-8 hero variant from the slug/title (for articles missing hero_variant). */
function deriveHeroVariant(post) {
  if (post?.hero_variant) return post.hero_variant;
  const src = String(post?.slug || post?.title || "");
  let h = 0;
  for (let i = 0; i < src.length; i++) h = (h * 31 + src.charCodeAt(i)) | 0;
  return (Math.abs(h) % 8) + 1;
}

/** Hero renderer — <img> when we have a real image, BrandedHero if the URL
 *  is known-broken or the network request fails at runtime. Guarantees no
 *  broken image icon ever renders on the article page. */
function HeroSection({ post }) {
  const [failed, setFailed] = React.useState(false);
  const useImage = post.hero_image && !isKnownBrokenHero(post.hero_image) && !failed;
  if (useImage) {
    return (
      <div className="mx-auto max-w-4xl px-6 lg:px-10 -mt-2 lg:-mt-6" data-testid="blog-hero-wrap">
        <figure className="aspect-[16/9] rounded-2xl overflow-hidden bg-[#F3F4F6] shadow-sm">
          <img
            src={post.hero_image}
            alt={post.hero_image_alt || post.title}
            className="w-full h-full object-cover"
            loading="eager"
            fetchPriority="high"
            decoding="async"
            data-testid="blog-hero-image"
            onError={() => setFailed(true)}
          />
        </figure>
      </div>
    );
  }
  // No usable image → render the brand-styled SVG hero. Never returns null so
  // the article always has a visual hero (fixes the "just alt text" bug).
  return (
    <div className="mx-auto max-w-4xl px-6 lg:px-10 -mt-2 lg:-mt-4" data-testid="blog-hero-wrap">
      <BrandedHero
        variant={deriveHeroVariant(post)}
        eyebrow={post.category}
        title={post.h1 || post.title}
      />
    </div>
  );
}

function formatDateLong(iso) {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" });
  } catch {
    return "";
  }
}

const readingMinutes = (words = 0) => Math.max(1, Math.round(words / 220));

const safeDecode = (s) => { try { return decodeURIComponent(s); } catch { return s; } };

export default function BlogPost() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [post, setPost] = useState(null);
  const [status, setStatus] = useState("loading");
  const unservedState = UNSERVED_STATE_GUIDES[safeDecode(post?.slug || slug)];

  useEffect(() => {
    setStatus("loading");
    window.scrollTo({ top: 0 });
    api.get(`/blog/${slug}`)
      .then((res) => {
        const data = res.data;
        if (!data || typeof data !== "object" || !data.slug) { setStatus("error"); return; }
        // API followed a consolidation 301: render the destination in place (canonical = destination)
        // and swap the address bar without a reload, so pre-renderers never snapshot a half-loaded page.
        if (safeDecode(data.slug) !== slug) window.history.replaceState(window.history.state, "", `/blog/${data.slug}`);
        setPost(data); setStatus("ready");
      })
      .catch((err) => { setStatus(err?.response?.status === 404 ? "notfound" : "error"); });
  }, [slug]);

  // Process article HTML once per post. Heavy: DOMParser + walk of every <p>/<h2>/<img>.
  const processed = useMemo(
    () => processArticleHtml(post?.body_html || "", { title: post?.h1 || post?.title }),
    [post?.body_html, post?.h1, post?.title],
  );

  const canonical = post ? `${SITE_URL}/blog/${post.slug}` : undefined;

  // Compose all three JSON-LD blocks: Article, BreadcrumbList, FAQPage.
  // We emit them as a graph so a single <script> tag covers everything.
  const jsonLd = useMemo(() => {
    if (!post) return undefined;
    const authorName = post.author?.name || "Instabridge Funding";
    const authorUrl = post.author?.url ? `${SITE_URL}${post.author.url}` : undefined;
    const reviewerName = post.reviewer?.name;
    const graph = [
      {
        "@type": "Article",
        headline: post.h1 || post.title,
        description: post.meta_description,
        image: post.hero_image ? [post.hero_image] : undefined,
        datePublished: post.published_at,
        dateModified: post.last_updated_iso || post.updated_at || post.fetched_at || post.published_at,
        author: {
          "@type": "Organization",
          name: authorName,
          ...(authorUrl ? { url: authorUrl } : {}),
        },
        ...(reviewerName
          ? {
              reviewedBy: { "@type": "Organization", name: reviewerName },
            }
          : {}),
        publisher: {
          "@type": "Organization",
          name: "Instabridge Funding",
          logo: { "@type": "ImageObject", url: `${SITE_URL}/og-image.png` },
        },
        mainEntityOfPage: { "@type": "WebPage", "@id": canonical },
        articleSection: post.category,
        wordCount: post.word_count || undefined,
      },
      {
        "@type": "BreadcrumbList",
        itemListElement: [
          { "@type": "ListItem", position: 1, name: "Home", item: SITE_URL },
          { "@type": "ListItem", position: 2, name: "Blog", item: `${SITE_URL}/blog` },
          post.category
            ? { "@type": "ListItem", position: 3, name: post.category, item: `${SITE_URL}/blog?category=${encodeURIComponent(post.category)}` }
            : null,
          { "@type": "ListItem", position: post.category ? 4 : 3, name: post.h1 || post.title, item: canonical },
        ].filter(Boolean),
      },
    ];
    if (processed.faqs.length) {
      graph.push({
        "@type": "FAQPage",
        mainEntity: processed.faqs.map((f) => ({
          "@type": "Question",
          name: f.q,
          acceptedAnswer: {
            "@type": "Answer",
            text: f.a.replace(/<[^>]+>/g, "").trim(),
          },
        })),
      });
    }
    return { "@context": "https://schema.org", "@graph": graph };
  }, [post, canonical, processed.faqs]);

  useSEO(
    // Prefer seo_title (short, ≤60 chars, optimized for the SERP) over the
    // long H1 title. Falls back to title if seo_title isn't set.
    post?.seo_title || post?.title || (status === "notfound" ? "Article not found" : "Loading…"),
    post?.meta_description || "",
    { canonical, ogImage: post?.hero_image, ogType: "article", jsonLd, noindex: status === "notfound" },
  );

  // Reading-progress bar (single rAF-throttled scroll listener).
  const [progress, setProgress] = useState(0);
  useEffect(() => {
    if (status !== "ready") return undefined;
    let ticking = false;
    const onScroll = () => {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(() => {
        ticking = false;
        const article = document.querySelector('[data-testid="blog-post-body"]');
        if (!article) return;
        const rect = article.getBoundingClientRect();
        const viewportH = window.innerHeight;
        const total = rect.height - viewportH;
        if (total <= 0) {
          setProgress(rect.top < 0 ? 100 : 0);
          return;
        }
        const scrolled = Math.max(0, Math.min(total, -rect.top));
        setProgress(Math.round((scrolled / total) * 100));
      });
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener("scroll", onScroll);
  }, [status]);

  if (status === "loading") {
    return (
      <div className="min-h-screen bg-white">
        <Navbar />
        <div className="flex items-center justify-center py-32 text-[#6B7280]">
          <Loader2 className="w-5 h-5 mr-2 animate-spin" /> Loading article…
        </div>
        <Footer />
      </div>
    );
  }
  if (status === "notfound") {
    return (
      <div className="min-h-screen bg-white">
        <Navbar />
        <section className="py-24 lg:py-32 text-center">
          <div className="mx-auto max-w-xl px-6">
            <div className="uppercase-label mb-5">404</div>
            <h1 className="font-display text-4xl text-[#0A2540] font-semibold">We can&apos;t find that article.</h1>
            <p className="mt-4 text-[#6B7280]">It may have been renamed or removed. Browse the full library to find what you&apos;re looking for.</p>
            <Link to="/blog" className="inline-block mt-7">
              <Button className="rounded-full h-12 px-7 bg-[#0A2540] hover:bg-[#001A3D]" data-testid="blog-back-to-index">Back to blog</Button>
            </Link>
          </div>
        </section>
        <Footer />
      </div>
    );
  }

  const updatedDate = post.updated_at && post.updated_at !== post.published_at
    ? formatDateLong(post.updated_at)
    : null;

  return (
    <div className="min-h-screen bg-white" data-testid="blog-post-page">
      <Navbar />

      {/* Reading progress — fixed strip under the navbar */}
      <div
        className="fixed top-[64px] left-0 right-0 h-[3px] bg-transparent z-40 pointer-events-none"
        aria-hidden="true"
      >
        <div
          className="h-full bg-gradient-to-r from-[#0066FF] to-[#00D1FF] transition-[width] duration-150 ease-out"
          style={{ width: `${progress}%` }}
          data-testid="blog-scroll-progress"
        />
      </div>

      {/* ─────────────────── HERO ─────────────────── */}
      <header className="border-b border-[#E5E7EB] bg-gradient-to-b from-[#F9FAFB] to-white">
        <div className="mx-auto max-w-3xl px-6 lg:px-10 pt-10 lg:pt-14 pb-10 lg:pb-14">
          <button
            onClick={() => navigate(-1)}
            className="inline-flex items-center gap-1.5 text-sm text-[#6B7280] hover:text-[#0A2540] mb-6 transition-colors"
            data-testid="blog-back-btn"
          >
            <ArrowLeft className="w-4 h-4" /> Back
          </button>

          <Breadcrumbs category={post.category} title={post.h1 || post.title} />

          <div className="flex flex-wrap items-center gap-3 text-xs text-[#6B7280] mb-4">
            <span className="uppercase tracking-wider font-medium text-[#0066FF]">{post.category}</span>
            <span>·</span>
            <span className="flex items-center gap-1.5">
              <Calendar className="w-3.5 h-3.5" /> {formatDateLong(post.published_at)}
            </span>
            <span>·</span>
            <span className="flex items-center gap-1.5">
              <Clock className="w-3.5 h-3.5" /> {readingMinutes(post.word_count)} min read
            </span>
            {updatedDate ? (
              <>
                <span>·</span>
                <span className="blog-updated-pill" data-testid="blog-updated-pill">
                  Updated {updatedDate}
                </span>
              </>
            ) : null}
            {post.author?.name ? (
              <>
                <span>·</span>
                <span className="flex items-center gap-1.5" data-testid="blog-post-author">
                  By <a href={post.author.url || "/for-attorneys"} className="font-medium text-[#0A2540] hover:text-[#0066FF]">{post.author.name}</a>
                </span>
              </>
            ) : null}
            {post.reviewer?.name ? (
              <>
                <span>·</span>
                <span data-testid="blog-post-reviewer">
                  Reviewed by <span className="font-medium text-[#0A2540]">{post.reviewer.name}</span>
                </span>
              </>
            ) : null}
          </div>

          <h1
            className="font-display text-3xl sm:text-4xl lg:text-[3.25rem] text-[#0A2540] font-semibold tracking-tight leading-[1.1]"
            data-testid="blog-post-title"
          >
            {post.h1 || post.title}
          </h1>

          {post.meta_description ? (
            <p className="mt-5 text-lg text-[#374151] leading-relaxed">{post.meta_description}</p>
          ) : null}

          <div className="mt-7">
            <ShareButtons url={canonical} title={post.h1 || post.title} />
          </div>
        </div>
      </header>

      {/* ─────────────────── HERO IMAGE ─────────────────── */}
      <HeroSection post={post} />



      {/* ─────────────────── BODY + STICKY TOC ─────────────────── */}
      <div className="mx-auto max-w-7xl px-6 lg:px-10 py-12 lg:py-16">
        <div className="lg:grid lg:grid-cols-[1fr_240px] lg:gap-14 xl:gap-20">
          <article className="mx-auto max-w-[680px] lg:mx-0 w-full min-w-0">
            <MobileTableOfContents items={processed.toc} />

            {unservedState && (
              <div className="mb-8 rounded-xl border border-[#E5E7EB] bg-[#F9FAFB] px-5 py-4 text-sm text-[#374151]" data-testid="blog-availability-note">
                <strong>Availability note:</strong> Instabridge does not currently offer funding in {unservedState}. This guide is provided for general educational purposes. See <Link to="/states" className="underline underline-offset-4">the states we currently serve</Link>.
              </div>
            )}

            <QuickAnswer text={processed.quickAnswer} />

            <BlogBody html={processed.html} />

            <FAQAccordion items={processed.faqs} />

            {/* CTA — uses the brand gradient + non-recourse copy */}
            <div className="mt-14 pt-10 border-t border-[#E5E7EB]">
              {unservedState ? (
              <div className="rounded-2xl bg-gradient-to-br from-[#0A2540] to-[#001A3D] text-white p-8 lg:p-10" data-testid="blog-cta-unserved">
                <div className="uppercase-label text-[#FAD28C] mb-3">Service availability</div>
                <h2 className="font-display text-2xl sm:text-3xl font-semibold mb-0">Not yet available in {unservedState}.</h2>
                <p className="mt-3 text-white/80 max-w-xl">
                  We're continually working to expand our service areas. Contact us to learn about future availability.
                </p>
                <div className="mt-6 flex flex-wrap gap-3">
                  <Link to="/contact">
                    <Button className="rounded-full h-12 px-7 bg-white text-[#0A2540] hover:bg-white/90 font-medium" data-testid="blog-cta-contact">
                      Contact us <ArrowRight className="w-4 h-4 ml-1.5" />
                    </Button>
                  </Link>
                  <Link to="/states">
                    <Button variant="ghost" className="rounded-full h-12 px-7 text-white hover:bg-white/10" data-testid="blog-cta-states">
                      States we serve
                    </Button>
                  </Link>
                </div>
              </div>
              ) : (
              <div className="rounded-2xl bg-gradient-to-br from-[#0A2540] to-[#001A3D] text-white p-8 lg:p-10">
                <div className="uppercase-label text-[#FAD28C] mb-3">Waiting on a settlement?</div>
                <h2 className="font-display text-2xl sm:text-3xl font-semibold mb-0">
                  Get cash today. Settled the right way.
                </h2>
                <p className="mt-3 text-white/80 max-w-xl">
                  Non-recourse advances from $500 to $25,000. Funds in 24–48 hours. Owe nothing if you lose.
                </p>
                <div className="mt-6 flex flex-wrap gap-3">
                  <Link to="/#apply">
                    <Button className="rounded-full h-12 px-7 bg-white text-[#0A2540] hover:bg-white/90 font-medium" data-testid="blog-cta-apply">
                      Apply now <ArrowRight className="w-4 h-4 ml-1.5" />
                    </Button>
                  </Link>
                  <Link to="/how-it-works">
                    <Button variant="ghost" className="rounded-full h-12 px-7 text-white hover:bg-white/10" data-testid="blog-cta-how">
                      How it works
                    </Button>
                  </Link>
                </div>
              </div>
              )}
            </div>

            <AuthorBlock
              url={canonical}
              title={post.h1 || post.title}
              updatedAt={updatedDate || formatDateLong(post.published_at)}
            />

            <PrevNextNav prev={post.prev} next={post.next} />
          </article>

          {/* Sticky desktop TOC */}
          <aside className="hidden lg:block">
            <div className="sticky top-[110px]">
              <TableOfContents items={processed.toc} />
            </div>
          </aside>
        </div>
      </div>

      {/* ─────────────────── RELATED CAROUSEL ─────────────────── */}
      <RelatedCarousel items={post.related} />

      <Footer />
    </div>
  );
}

/**
 * BlogBody — splits the (processed) HTML on the literal "{{CALCULATOR_WIDGET}}"
 * placeholder and renders the interactive calculator inline.
 */
function BlogBody({ html }) {
  const parts = useMemo(() => (html || "").split(/\{\{CALCULATOR_WIDGET\}\}/g), [html]);
  if (parts.length === 1) {
    return (
      <div
        className="blog-prose"
        dangerouslySetInnerHTML={{ __html: html }}
        data-testid="blog-post-body"
      />
    );
  }
  return (
    <div data-testid="blog-post-body">
      {parts.map((chunk, i) => (
        <React.Fragment key={i}>
          <div className="blog-prose" dangerouslySetInnerHTML={{ __html: chunk }} />
          {i < parts.length - 1 && <PreSettlementCalculator />}
        </React.Fragment>
      ))}
    </div>
  );
}
